import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  bookingApi,
  vehicleTypesApi,
  addressesApi,
  type Address,
  type BookingTripResult,
  type TripStopInput,
  type VehicleType,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { useAuth } from '@/auth/AuthContext';
import { PortalLayout } from '@/components/Layout';
import { PortalNav } from '@/components/Nav';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import {
  StopAddressPicker,
  isAddressSelectionReady,
  resolveAddressSelection,
  type AddressSelection,
} from '@/components/StopAddressPicker';
import { formatManilaLocalDateTime, parseManilaLocalDateTime } from '@dispatchdash/shared';
import { formatPeso } from '@/lib/format';

export function PortalBook() {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const clientId = user?.clientId;
  const canBook = hasPermission('portal.book');

  useEffect(() => {
    if (!canBook) navigate('/portal', { replace: true });
  }, [canBook, navigate]);

  const {
    data: vehicleTypes,
    loading: vehicleTypesLoading,
    error: vehicleTypesError,
    reload: reloadVehicleTypes,
  } = useAsync(() => vehicleTypesApi.list(), []);
  const activeVehicleTypes = useMemo(
    () => (vehicleTypes ?? []).filter((v: VehicleType) => v.status === 'active'),
    [vehicleTypes],
  );

  const [addresses, setAddresses] = useState<Address[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const [addressesError, setAddressesError] = useState(false);

  useEffect(() => {
    if (!canBook || !clientId) {
      setAddressesLoading(false);
      return;
    }
    setAddressesLoading(true);
    setAddressesError(false);
    addressesApi
      .list(clientId)
      .then((rows) => setAddresses(rows))
      .catch(() => {
        setAddresses([]);
        setAddressesError(true);
      })
      .finally(() => setAddressesLoading(false));
  }, [clientId]);

  const [form, setForm] = useState({
    vehicleTypeId: '',
    scheduledPickupAt: formatManilaLocalDateTime(new Date(Date.now() + 3 * 60 * 60 * 1000)),
    pickup: null as AddressSelection | null,
    drop: null as AddressSelection | null,
    extraDrops: [] as Array<AddressSelection | null>,
    cargoDescription: '',
    specialInstructions: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<BookingTripResult | null>(null);

  // Preselect the vehicle type when there is only one to choose from.
  useEffect(() => {
    if (activeVehicleTypes.length === 1 && !form.vehicleTypeId) {
      setForm((f) => ({ ...f, vehicleTypeId: activeVehicleTypes[0].id }));
    }
  }, [activeVehicleTypes, form.vehicleTypeId]);

  const bookAnother = () => {
    setResult(null);
    setError('');
    setForm((f) => ({
      ...f,
      scheduledPickupAt: formatManilaLocalDateTime(new Date(Date.now() + 3 * 60 * 60 * 1000)),
      pickup: null,
      drop: null,
      extraDrops: [],
      cargoDescription: '',
      specialInstructions: '',
    }));
  };

  const addExtraDrop = () => setForm((f) => ({ ...f, extraDrops: [...f.extraDrops, null] }));
  const updateExtraDrop = (index: number, value: AddressSelection | null) => {
    setForm((f) => {
      const next = [...f.extraDrops];
      next[index] = value;
      return { ...f, extraDrops: next };
    });
  };
  const removeExtraDrop = (index: number) =>
    setForm((f) => ({ ...f, extraDrops: f.extraDrops.filter((_, i) => i !== index) }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!canBook) {
      setError('Your login can view trips but not book them. Ask your company\'s portal admin for booking access.');
      return;
    }
    if (!clientId) {
      setError('Your login isn\'t linked to a company account yet. Please contact your account manager.');
      return;
    }
    if (!isAddressSelectionReady(form.pickup) || !isAddressSelectionReady(form.drop)) {
      setError('Choose or type a pickup address and at least one drop-off address.');
      return;
    }

    setLoading(true);
    try {
      const remember = (created: Address) => {
        setAddresses((prev) => (prev.some((a) => a.id === created.id) ? prev : [...prev, created]));
      };

      const dropSelections = [form.drop!, ...form.extraDrops.filter(isAddressSelectionReady)] as AddressSelection[];
      const pickupFields = await resolveAddressSelection(form.pickup!, clientId, remember);
      const dropFields = await Promise.all(
        dropSelections.map((sel) => resolveAddressSelection(sel, clientId, remember)),
      );

      const stops: TripStopInput[] = [
        { sequence: 1, type: 'pickup', ...pickupFields },
        ...dropFields.map((fields, i) => ({
          sequence: i + 2,
          type: 'drop' as const,
          ...fields,
        })),
      ];

      const trip = await bookingApi.createPortalTrip({
        scheduledPickupAt: parseManilaLocalDateTime(form.scheduledPickupAt).toISOString(),
        vehicleTypeId: form.vehicleTypeId,
        stops,
        cargoDescription: form.cargoDescription || undefined,
        specialInstructions: form.specialInstructions || undefined,
      });
      setResult(trip);
    } catch (err) {
      setError((err as { message?: string }).message ?? 'We couldn\'t create this booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PortalLayout>
      <div className="page">
        <div className="portal-nav">
          <PortalNav variant="top" />
        </div>

        {!canBook ? (
          <div className="page-header">
            <h1>Book a trip</h1>
            <p className="text-muted">Your login can view trips but not book them. Taking you to your trips…</p>
          </div>
        ) : (
          <>
        <div className="page-header row-between">
          <div>
            <h1>Book a trip</h1>
            <p className="text-muted">Search your address book or type a new pickup/drop location</p>
          </div>
          <Button variant="secondary" type="button" onClick={() => navigate('/portal/book/bulk')}>
            Bulk upload
          </Button>
        </div>

        {result ? (
          <div className="book-form quote-panel">
            <h3>Trip {result.tripNumber} booked</h3>
            <p>
              We&apos;ll review and confirm your booking, then notify you when a truck is assigned.
              You can follow its progress from your trips list.
            </p>
            {result.quoteRequired && !result.quoteAccepted ? (
              <div className="price-quote">
                <p className="price-note text-muted">
                  This route needs a custom quote. We&apos;ll send you a price to accept before the trip is confirmed.
                </p>
              </div>
            ) : result.quotedAmount != null && result.quotedAmount !== '' ? (
              <div className="price-quote">
                <div className="price-total">
                  <span>Estimated price</span>
                  <strong>{formatPeso(result.quotedAmount)}</strong>
                </div>
                <p className="price-note text-muted">
                  The final price may change if the route, waiting time or extra services differ from the booking.
                </p>
              </div>
            ) : null}
            <div className="form-actions">
              <Button variant="secondary" type="button" onClick={bookAnother}>Book another</Button>
              <Button type="button" onClick={() => navigate(`/portal/trips/${result.id}`)}>View trip</Button>
            </div>
          </div>
        ) : (
          <form className="book-form" onSubmit={handleSubmit}>
            {error && <div className="form-error" role="alert">{error}</div>}
            {addressesError && (
              <p className="input-hint" role="status" style={{ marginBottom: 'var(--space-md)' }}>
                Saved addresses couldn&apos;t be loaded; you can still type an address.
              </p>
            )}

            <div className="form-row">
              <StopAddressPicker
                label="Pickup address"
                clientId={clientId}
                addresses={addresses}
                value={form.pickup}
                onChange={(pickup) => setForm({ ...form, pickup })}
                disabled={addressesLoading || !clientId}
                required
              />
              <StopAddressPicker
                label="Drop-off address"
                clientId={clientId}
                addresses={addresses}
                value={form.drop}
                onChange={(drop) => setForm({ ...form, drop })}
                disabled={addressesLoading || !clientId}
                required
              />
            </div>

            {form.extraDrops.map((selection, i) => (
              <div className="form-row extra-drop-row" key={i}>
                <StopAddressPicker
                  label={`Extra drop-off ${i + 1}`}
                  clientId={clientId}
                  addresses={addresses}
                  value={selection}
                  onChange={(value) => updateExtraDrop(i, value)}
                  disabled={addressesLoading || !clientId}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="extra-drop-remove"
                  aria-label={`Remove extra drop-off ${i + 1}`}
                  onClick={() => removeExtraDrop(i)}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button type="button" variant="secondary" size="sm" onClick={addExtraDrop}>+ Add another drop-off</Button>

            <p className="input-hint" style={{ marginTop: 'var(--space-sm)' }}>
              Type to search saved locations, or enter a new address and tick “Save to address book”.
            </p>

            <div className="form-row" style={{ marginTop: 'var(--space-md)' }}>
              <Select
                label="Vehicle type"
                value={form.vehicleTypeId}
                onChange={(e) => setForm({ ...form, vehicleTypeId: e.target.value })}
                required
              >
                <option value="">
                  {vehicleTypesLoading ? 'Loading…' : activeVehicleTypes.length ? 'Select…' : 'None available'}
                </option>
                {activeVehicleTypes.map((v: VehicleType) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </Select>
              <Input
                label="Pickup date and time"
                type="datetime-local"
                value={form.scheduledPickupAt}
                min={formatManilaLocalDateTime(new Date())}
                onChange={(e) => setForm({ ...form, scheduledPickupAt: e.target.value })}
                hint="Philippine time"
                required
              />
            </div>
            {vehicleTypesError ? (
              <p className="form-error" role="alert">
                Vehicle types couldn&apos;t be loaded.{' '}
                <Button type="button" variant="secondary" size="sm" onClick={() => reloadVehicleTypes()}>Retry</Button>
              </p>
            ) : !vehicleTypesLoading && activeVehicleTypes.length === 0 ? (
              <p className="form-error" role="alert">
                No vehicle types are available for booking right now. Please contact your account manager.
              </p>
            ) : null}

            <Input
              label="Cargo summary"
              value={form.cargoDescription}
              onChange={(e) => setForm({ ...form, cargoDescription: e.target.value })}
              placeholder="Describe your cargo"
              required
            />

            <Input
              label="Special instructions"
              value={form.specialInstructions}
              onChange={(e) => setForm({ ...form, specialInstructions: e.target.value })}
              placeholder="Notes for the crew…"
            />

            <div className="form-actions">
              <Button variant="secondary" onClick={() => navigate('/portal')} type="button">Cancel</Button>
              <Button type="submit" loading={loading} disabled={!canBook}>Confirm booking</Button>
            </div>
          </form>
        )}
          </>
        )}

        <style>{`
          .book-form {
            max-width: 640px;
            background: var(--color-surface);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            padding: var(--space-xl);
          }
          .form-error {
            background: #FDE8E8;
            color: var(--color-urgent);
            padding: var(--space-sm) var(--space-md);
            border-radius: var(--radius-sm);
            margin-bottom: var(--space-md);
          }
          .price-quote {
            background: var(--color-sand-light);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            padding: var(--space-lg);
            margin: var(--space-lg) 0;
          }
          .price-total {
            display: flex;
            justify-content: space-between;
            font-size: 1.25rem;
            margin-bottom: var(--space-md);
          }
          .price-total strong {
            font-family: var(--font-display);
            color: var(--color-forest);
          }
          .price-note { font-size: 0.75rem; }
          .form-actions {
            display: flex;
            gap: var(--space-md);
            margin-top: var(--space-lg);
          }
          .quote-panel h3 { color: var(--color-forest); margin-bottom: var(--space-md); }
          .form-row {
            display: flex;
            gap: var(--space-md);
            align-items: flex-start;
          }
          .form-row > * { flex: 1; min-width: 0; }
          .extra-drop-row { align-items: flex-end; }
          .form-row > .extra-drop-remove { flex: 0 0 auto; margin-bottom: var(--space-md); }
          @media (max-width: 640px) {
            .book-form { padding: var(--space-lg) var(--space-md); }
            .form-row { flex-direction: column; align-items: stretch; gap: 0; }
            .form-row > .extra-drop-remove { align-self: flex-end; margin-bottom: var(--space-md); }
            .form-actions { flex-wrap: wrap; }
          }
        `}</style>
      </div>
    </PortalLayout>
  );
}
